import { plain } from '../../lib/labels';
import { useEffect, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { UserName } from '../../lib/listenerName';

/**
 * FOUR-RULE referral config (2026-10). Which rule applies is decided by BOTH
 * roles at settlement; every threshold and reward is editable here and applied
 * at runtime. ₹ amounts travel as PAISE over the API; coin rewards are exact
 * coin quantities and are never converted to/from rupees.
 */
interface ReferralConfig {
  enabled: boolean;
  expiryDays: number;
  maxRewardedPerReferrer: number;
  /** Days to hold a plain-user referral so a listener application can win. */
  listenerGraceDays: number;
  /** Listener→listener: talk time → one-off ₹ earning. */
  ll: { enabled: boolean; talkMinutes: number; rewardPaise: number };
  /** Listener→user: every cumulative recharge threshold → ₹ earning. */
  lu: { enabled: boolean; thresholdPaise: number; rewardPaise: number; incremental: boolean };
  /** User→user: one recharge ≥ min → exact coins. */
  uu: { enabled: boolean; minRechargePaise: number; rewardCoins: number };
  /** User→listener: talk time → exact coins. */
  ul: { enabled: boolean; talkMinutes: number; rewardCoins: number };
}

/** The config form's values: rupee fields in WHOLE RUPEES for the operator. */
export interface ReferralConfigForm {
  enabled: boolean;
  expiryDays: number;
  maxRewardedPerReferrer: number;
  listenerGraceDays: number;
  llEnabled: boolean;
  llTalkMinutes: number;
  llRewardRupees: number;
  luEnabled: boolean;
  luThresholdRupees: number;
  luRewardRupees: number;
  luIncremental: boolean;
  uuEnabled: boolean;
  uuMinRechargeRupees: number;
  uuRewardCoins: number;
  ulEnabled: boolean;
  ulTalkMinutes: number;
  ulRewardCoins: number;
}

/** API (paise) → form (whole ₹, rounded up so the displayed gate never understates). */
export function configToForm(c: ReferralConfig): ReferralConfigForm {
  const r = (paise: number) => Math.ceil(paise / 100);
  return {
    enabled: c.enabled,
    expiryDays: c.expiryDays,
    maxRewardedPerReferrer: c.maxRewardedPerReferrer,
    listenerGraceDays: c.listenerGraceDays,
    llEnabled: c.ll.enabled,
    llTalkMinutes: c.ll.talkMinutes,
    llRewardRupees: r(c.ll.rewardPaise),
    luEnabled: c.lu.enabled,
    luThresholdRupees: r(c.lu.thresholdPaise),
    luRewardRupees: r(c.lu.rewardPaise),
    luIncremental: c.lu.incremental,
    uuEnabled: c.uu.enabled,
    uuMinRechargeRupees: r(c.uu.minRechargePaise),
    uuRewardCoins: c.uu.rewardCoins,
    ulEnabled: c.ul.enabled,
    ulTalkMinutes: c.ul.talkMinutes,
    ulRewardCoins: c.ul.rewardCoins,
  };
}

/** Form (whole ₹) → API body (paise; coin quantities pass through untouched). */
export function formToConfig(f: ReferralConfigForm): ReferralConfig {
  const p = (rupees: number) => Math.round(rupees * 100);
  return {
    enabled: f.enabled,
    expiryDays: f.expiryDays,
    maxRewardedPerReferrer: f.maxRewardedPerReferrer,
    listenerGraceDays: f.listenerGraceDays,
    ll: { enabled: f.llEnabled, talkMinutes: f.llTalkMinutes, rewardPaise: p(f.llRewardRupees) },
    lu: {
      enabled: f.luEnabled,
      thresholdPaise: p(f.luThresholdRupees),
      rewardPaise: p(f.luRewardRupees),
      incremental: f.luIncremental,
    },
    uu: {
      enabled: f.uuEnabled,
      minRechargePaise: p(f.uuMinRechargeRupees),
      rewardCoins: f.uuRewardCoins,
    },
    ul: { enabled: f.ulEnabled, talkMinutes: f.ulTalkMinutes, rewardCoins: f.ulRewardCoins },
  };
}

/** What a referral is waiting for / qualified with. */
export interface ReferralQualification {
  kind:
    | 'talk_minutes'
    | 'recharge_rupees'
    | 'single_recharge_rupees'
    | 'recharge_coins'
    | 'connected_calls'
    | 'listener_review';
  required: number;
  progress: number | null;
}

export interface AdminReferralRow {
  id: string;
  status: 'pending' | 'rewarded' | 'capped' | 'expired' | 'void' | 'circular';
  source: 'manual' | 'link';
  referrerHandle: string;
  refereeHandle: string;
  /** Absent on older APIs — the row then reads by @handle, unlinked. */
  referrerId?: string | null;
  refereeId?: string | null;
  referrerDisplayName?: string | null;
  refereeDisplayName?: string | null;
  createdAt: string;
  expiresAt: string | null;
  qualifiedAt: string | null;
  rewardedAt: string | null;
  qualification: ReferralQualification | null;
  referrerReward: { kind: 'none' | 'coins' | 'listener_earning'; coins: number; paise: string };
}

/** "₹320 / ₹1,000 recharged", "23 / 60 talk minutes", … */
export function qualificationText(q: ReferralQualification | null): string {
  if (!q) return 'No requirement';
  if (q.kind === 'listener_review') return 'Waiting for the listener application decision';
  const fmt: Record<Exclude<ReferralQualification['kind'], 'listener_review'>, (n: number) => string> = {
    talk_minutes: (n) => `${n}`,
    recharge_rupees: (n) => `₹${n.toLocaleString('en-IN')}`,
    single_recharge_rupees: (n) => `₹${n.toLocaleString('en-IN')}`,
    recharge_coins: (n) => `${n}`,
    connected_calls: (n) => `${n}`,
  };
  const unit: Record<Exclude<ReferralQualification['kind'], 'listener_review'>, string> = {
    talk_minutes: 'talk minutes',
    recharge_rupees: 'recharged',
    single_recharge_rupees: 'in one recharge',
    recharge_coins: 'coins recharged',
    connected_calls: 'connected calls',
  };
  const f = fmt[q.kind];
  return q.progress === null
    ? `Needs ${f(q.required)} ${unit[q.kind]}`
    : `${f(q.progress)} / ${f(q.required)} ${unit[q.kind]}`;
}

/** The status label: a pending referral whose progress already meets the
 *  requirement is QUALIFIED (it pays on the next event / worker pass). */
export function referralStatusLabel(r: Pick<AdminReferralRow, 'status' | 'qualification'>): string {
  const q = r.qualification;
  if (r.status === 'pending' && q && q.kind !== 'listener_review' && q.progress !== null && q.progress >= q.required) {
    return 'Qualified';
  }
  return r.status.charAt(0).toUpperCase() + r.status.slice(1);
}

interface ReferralAnalytics {
  total: number;
  pending: number;
  rewarded: number;
  /** Referrer skipped because they were at their cap. Pays nobody now. */
  capped: number;
  expired: number;
  void: number;
  /** Coins credited to user referrers across all settled referrals. */
  coinsPaid: number;
  /** Listener earnings credited to referrers, in paise. */
  earningsPaidPaise: string;
  /** Settled referrals whose referee was an approved listener. */
  listenerConversions: number;
  conversionRate: number;
}

interface TopReferrer {
  userId: string;
  handle: string;
  /** The user's own Display Name — absent on older APIs. */
  displayName?: string | null;
  role: 'user' | 'listener';
  totalReferrals: number;
  successfulReferrals: number;
  coinsEarned: number;
  earningsPaise: string;
}

/** Paise → ₹, whole rupees where exact. */
function rupees(paise: number | string): string {
  const p = typeof paise === 'string' ? Number(paise) : paise;
  if (!Number.isFinite(p)) return '₹0';
  return p % 100 === 0 ? `₹${(p / 100).toLocaleString('en-IN')}` : `₹${(p / 100).toFixed(2)}`;
}

const RUPEE_FIELD = { min: 0, max: 1_000_000, style: { width: '100%' }, addonBefore: '₹' } as const;

/**
 * Referral rewards: the four role-pair rules + funnel analytics + top
 * referrers. Config applies immediately (no deploy). Admin / ops / finance only.
 */
export function ReferralPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<ReferralConfigForm>();

  const cfg = useQuery({
    queryKey: ['admin', 'referral', 'config'],
    queryFn: async () => (await api.get<ReferralConfig>('/admin/referral/config')).data,
  });
  const analytics = useQuery({
    queryKey: ['admin', 'referral', 'analytics'],
    queryFn: async () => (await api.get<ReferralAnalytics>('/admin/referral/analytics')).data,
  });
  const top = useQuery({
    queryKey: ['admin', 'referral', 'top-referrers'],
    queryFn: async () =>
      (await api.get<{ items: TopReferrer[] }>('/admin/referral/top-referrers?limit=20')).data.items,
  });

  useEffect(() => {
    if (cfg.data) form.setFieldsValue(configToForm(cfg.data));
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (values: ReferralConfigForm) =>
      (await api.post<ReferralConfig>('/admin/referral/config', formToConfig(values))).data,
    onSuccess: (data) => {
      message.success('Referral settings updated — applied immediately.');
      form.setFieldsValue(configToForm(data));
      qc.invalidateQueries({ queryKey: ['admin', 'referral', 'config'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed',
      ),
  });

  const a = analytics.data;
  // Live preview sentence per rule, so a typo is visible before Save.
  const w = Form.useWatch([], form) as ReferralConfigForm | undefined;

  const ruleCard = (
    title: string,
    enabledField: keyof ReferralConfigForm,
    summary: string,
    body: React.ReactNode,
  ) => (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={title}
      extra={
        <Form.Item name={enabledField} valuePropName="checked" noStyle>
          <Switch checkedChildren="On" unCheckedChildren="Off" />
        </Form.Item>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {summary}
      </Typography.Paragraph>
      {body}
    </Card>
  );

  const intRule = (max: number) => [{ required: true, type: 'number' as const, min: 0, max }];

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>Referral rewards</Typography.Title>
      <Typography.Paragraph type="secondary">
        Four independent rules, one per referrer→referred role pair, paid <strong>to the referrer
        only</strong> once the referred person qualifies — the referred person never receives
        anything. Users are paid the <strong>exact configured coin quantity</strong> (never converted
        from rupees); listeners are paid money into their earnings wallet (withdrawable, inside the
        TDS path). Roles are read at payout time: an approved, unsuspended listener counts as a
        listener, everyone else as a user.
      </Typography.Paragraph>

      {/* Funnel analytics */}
      <Card loading={analytics.isLoading} style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={6}><Statistic title="Total" value={a?.total ?? 0} /></Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="Applied, but not paid yet: the referred person has not qualified (recharge / talk time), or their listener application is still in review.">
              <Statistic title="Pending" value={a?.pending ?? 0} />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Statistic title="Rewarded" value={a?.rewarded ?? 0} valueStyle={{ color: '#10B981' }} />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="The referral completed, but the referrer had already hit their cap so nobody was paid.">
              <Statistic title="Capped" value={a?.capped ?? 0} />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="The referred person never qualified in time (or an application was left in review past the expiry).">
              <Statistic title="Expired" value={a?.expired ?? 0} />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}><Statistic title="Void" value={a?.void ?? 0} /></Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="Coins credited to USER referrers across all settled referrals. Already in their wallets — this reconciles against the ledger (reason referral_bonus).">
              <Statistic title="Coins paid" value={a?.coinsPaid ?? 0} valueStyle={{ color: '#10B981' }} />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="Money credited to LISTENER referrers as earnings (ledger reason referral_earning), including every repeat recharge threshold. Withdrawable through the normal payout flow.">
              <Statistic
                title="Listener earnings paid"
                value={rupees(a?.earningsPaidPaise ?? 0)}
                valueStyle={{ color: '#10B981' }}
              />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="Settled referrals whose referred user was an approved listener — referrals that produced a listener rather than a spender.">
              <Statistic title="Listener conversions" value={a?.listenerConversions ?? 0} />
            </Tooltip>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Tooltip title="Rewarded + capped, over all non-void referrals.">
              <Statistic
                title="Conversion"
                value={((a?.conversionRate ?? 0) * 100).toFixed(1)}
                suffix="%"
              />
            </Tooltip>
          </Col>
        </Row>
      </Card>

      {/* Config form */}
      <Card title="Referral rules" loading={cfg.isLoading} style={{ maxWidth: 860, marginBottom: 20 }}>
        <Form<ReferralConfigForm> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item name="enabled" label="Referrals enabled" valuePropName="checked"
            tooltip="Global kill switch — off = no codes applied and no payouts (including repeat recharge thresholds).">
            <Switch />
          </Form.Item>

          {ruleCard(
            'A — Listener refers a listener',
            'llEnabled',
            `The referred listener must complete ${w?.llTalkMinutes ?? 60} minutes of connected-call talk time (ringing and online time never count); the referring listener then earns ₹${w?.llRewardRupees ?? 300} once, into their earnings wallet.`,
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="llTalkMinutes" label="Required talk time" rules={intRule(100000)}
                  tooltip="Cumulative connected-call minutes the referred listener must take (backend call records only).">
                  <InputNumber min={0} max={100000} style={{ width: '100%' }} addonAfter="minutes" data-testid="ll-talk-minutes" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="llRewardRupees" label="Referrer reward (money)" rules={intRule(1000000)}
                  tooltip="Credited once as a listener EARNING (reason referral_earning).">
                  <InputNumber {...RUPEE_FIELD} data-testid="ll-reward-rupees" />
                </Form.Item>
              </Col>
            </Row>,
          )}

          {ruleCard(
            'B — Listener refers a normal user',
            'luEnabled',
            `Every ₹${(w?.luThresholdRupees ?? 1000).toLocaleString('en-IN')} the referred user recharges (successful, verified recharges only, cumulative) earns the referring listener ₹${w?.luRewardRupees ?? 100}${(w?.luIncremental ?? true) ? ' — again at ₹' + ((w?.luThresholdRupees ?? 1000) * 2).toLocaleString('en-IN') + ', ₹' + ((w?.luThresholdRupees ?? 1000) * 3).toLocaleString('en-IN') + ', and so on' : ' (first threshold only)'}.`,
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="luThresholdRupees" label="Cumulative recharge threshold" rules={intRule(10000000)}
                  tooltip="Captured recharges since the referral add up; refunded, failed and pending payments never count.">
                  <InputNumber {...RUPEE_FIELD} data-testid="lu-threshold-rupees" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="luRewardRupees" label="Reward per threshold (money)" rules={intRule(1000000)}
                  tooltip="Credited as a listener EARNING each time a threshold completes. Each threshold is paid exactly once — duplicate payment webhooks cannot pay it twice.">
                  <InputNumber {...RUPEE_FIELD} data-testid="lu-reward-rupees" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="luIncremental" label="Incremental rewards" valuePropName="checked"
                  tooltip="On: ₹1,000 → reward, ₹2,000 → another, ₹3,000 → another… Off: only the first threshold pays.">
                  <Switch data-testid="lu-incremental" />
                </Form.Item>
              </Col>
            </Row>,
          )}

          {ruleCard(
            'C — Normal user refers a normal user',
            'uuEnabled',
            `The referred user must make ONE successful recharge of at least ₹${w?.uuMinRechargeRupees ?? 149}; the referrer then gets exactly ${w?.uuRewardCoins ?? 100} coins, once per referral. A bigger recharge still pays the same ${w?.uuRewardCoins ?? 100} coins — the amount gates, it never scales the reward.`,
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="uuMinRechargeRupees" label="Minimum qualifying recharge" rules={intRule(10000000)}
                  tooltip="A SINGLE recharge must reach this; smaller recharges do not add up for this rule.">
                  <InputNumber {...RUPEE_FIELD} data-testid="uu-min-recharge-rupees" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="uuRewardCoins" label="Referrer reward (coins)" rules={intRule(1000000)}
                  tooltip="EXACT coin quantity credited to the referrer's coin wallet — never converted from rupees.">
                  <InputNumber min={0} max={1000000} style={{ width: '100%' }} addonAfter="coins" data-testid="uu-reward-coins" />
                </Form.Item>
              </Col>
            </Row>,
          )}

          {ruleCard(
            'D — Normal user refers a listener',
            'ulEnabled',
            `The referred listener must complete ${w?.ulTalkMinutes ?? 60} minutes of connected-call talk time; the referring user then gets exactly ${w?.ulRewardCoins ?? 200} coins, once.`,
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="ulTalkMinutes" label="Required talk time" rules={intRule(100000)}
                  tooltip="Cumulative connected-call minutes the referred listener must take (backend call records only).">
                  <InputNumber min={0} max={100000} style={{ width: '100%' }} addonAfter="minutes" data-testid="ul-talk-minutes" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ulRewardCoins" label="Referrer reward (coins)" rules={intRule(1000000)}
                  tooltip="EXACT coin quantity credited to the referrer's coin wallet — never converted from rupees.">
                  <InputNumber min={0} max={1000000} style={{ width: '100%' }} addonAfter="coins" data-testid="ul-reward-coins" />
                </Form.Item>
              </Col>
            </Row>,
          )}

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Programme limits
          </Typography.Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="expiryDays" label="Expiry (days)"
                tooltip="A referral whose referred person has not qualified after this many days is EXPIRED and pays nothing. Already-rewarded referrals are never reversed; a listener→user referral that has already paid keeps accruing recharge thresholds."
                rules={[{ required: true, type: 'number', min: 1, max: 365 }]}>
                <InputNumber min={1} max={365} style={{ width: '100%' }} addonAfter="days" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxRewardedPerReferrer" label="Max rewarded per referrer"
                tooltip="0 = unlimited. Over the cap the referral still completes but nobody is paid (status: capped)."
                rules={[{ required: true, type: 'number', min: 0 }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="listenerGraceDays" label="Listener grace window"
                tooltip="0 (default) = a normal-user referral starts counting its qualification as soon as the code is applied. Raise it to hold the check for N days, so someone who applies to become a listener right after entering the code is judged as a listener."
                rules={[{ required: true, type: 'number', min: 0, max: 30 }]}>
                <InputNumber min={0} max={30} style={{ width: '100%' }} addonAfter="days" />
              </Form.Item>
            </Col>
          </Row>

          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert type="info" showIcon
              message="Changes take effect within ~60s (config cache TTL) and apply to FUTURE eligibility checks only — an edit never re-prices a referral that has already paid out, and never issues historical rewards retroactively." />
            <Can permission="referral.edit">
              <Button type="primary" htmlType="submit" loading={save.isPending}>Save settings</Button>
            </Can>
          </Space>
        </Form>
      </Card>

      <ReferralsCard />

      {/* Top referrers */}
      <Card title="Top referrers" loading={top.isLoading}>
        <Table<TopReferrer>
          rowKey="userId"
          dataSource={top.data ?? []}
          pagination={false}
          size="small"
          columns={[
            {
              title: 'User',
              dataIndex: 'handle',
              key: 'handle',
              render: (_: unknown, r) => <UserName handle={r.handle} displayName={r.displayName} userId={r.userId} />,
            },
            {
              title: 'Role',
              dataIndex: 'role',
              key: 'role',
              render: (role: TopReferrer['role']) => (
                <Tag color={role === 'listener' ? 'purple' : 'blue'}>{plain(role)}</Tag>
              ),
            },
            { title: 'Total referrals', dataIndex: 'totalReferrals', key: 'totalReferrals' },
            { title: 'Successful', dataIndex: 'successfulReferrals', key: 'successfulReferrals' },
            {
              title: 'Earned',
              key: 'earned',
              // Each referrer earned in the currency of their own role, so show the
              // one that applies rather than a coins column full of zeroes.
              render: (_: unknown, r: TopReferrer) =>
                r.role === 'listener' ? rupees(r.earningsPaise) : `${r.coinsEarned} coins`,
            },
          ]}
        />
      </Card>
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  Pending: 'gold',
  Qualified: 'cyan',
  Rewarded: 'green',
  Capped: 'orange',
  Expired: 'default',
  Void: 'default',
  Circular: 'red',
};

/** Every referral with its qualification progress. */
function ReferralsCard() {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const list = useQuery({
    queryKey: ['admin', 'referral', 'referrals', status ?? 'all', cursor ?? ''],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '25' });
      if (status) params.set('status', status);
      if (cursor) params.set('cursor', cursor);
      return (
        await api.get<{ items: AdminReferralRow[]; nextCursor: string | null }>(
          `/admin/referral/referrals?${params.toString()}`,
        )
      ).data;
    },
  });
  const filters: Array<[string | undefined, string]> = [
    [undefined, 'All'],
    ['pending', 'Pending'],
    ['rewarded', 'Rewarded'],
    ['expired', 'Expired'],
    ['capped', 'Capped'],
    ['void', 'Void'],
  ];
  return (
    <Card title="Referrals" style={{ marginBottom: 20 }} data-testid="referrals-card">
      <Space wrap style={{ marginBottom: 12 }}>
        {filters.map(([value, label]) => (
          <Button
            key={label}
            size="small"
            type={status === value ? 'primary' : 'default'}
            onClick={() => {
              setStatus(value);
              setCursor(undefined);
            }}
          >
            {label}
          </Button>
        ))}
      </Space>
      <Table<AdminReferralRow>
        rowKey="id"
        size="small"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={false}
        scroll={{ x: 900 }}
        columns={[
          {
            title: 'Referrer',
            dataIndex: 'referrerHandle',
            key: 'referrer',
            render: (_: unknown, r) => (
              <UserName handle={r.referrerHandle} displayName={r.referrerDisplayName} userId={r.referrerId} />
            ),
          },
          {
            title: 'Referred',
            dataIndex: 'refereeHandle',
            key: 'referee',
            render: (_: unknown, r) => (
              <UserName handle={r.refereeHandle} displayName={r.refereeDisplayName} userId={r.refereeId} />
            ),
          },
          {
            title: 'Status',
            key: 'status',
            render: (_: unknown, r) => {
              const label = referralStatusLabel(r);
              return <Tag color={STATUS_COLORS[label] ?? 'default'}>{label}</Tag>;
            },
          },
          {
            title: 'Qualification',
            key: 'qualification',
            render: (_: unknown, r) => qualificationText(r.qualification),
          },
          {
            title: 'Referrer paid',
            key: 'paid',
            render: (_: unknown, r) =>
              r.referrerReward.kind === 'coins'
                ? `${r.referrerReward.coins} coins`
                : r.referrerReward.kind === 'listener_earning'
                  ? rupees(r.referrerReward.paise)
                  : '—',
          },
          { title: 'Source', dataIndex: 'source', key: 'source' },
          {
            title: 'Applied',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: 'Expires / paid',
            key: 'when',
            render: (_: unknown, r) =>
              r.rewardedAt
                ? `Paid ${new Date(r.rewardedAt).toLocaleString()}`
                : r.expiresAt
                  ? `Expires ${new Date(r.expiresAt).toLocaleDateString()}`
                  : '—',
          },
        ]}
      />
      <Space style={{ marginTop: 12 }}>
        <Button size="small" disabled={!cursor} onClick={() => setCursor(undefined)}>
          First page
        </Button>
        <Button
          size="small"
          disabled={!list.data?.nextCursor}
          onClick={() => setCursor(list.data?.nextCursor ?? undefined)}
        >
          Next page
        </Button>
      </Space>
    </Card>
  );
}
